import Schema from '../Schema';

import {
  STATE,
  STATE_PATTERN
} from '../../../../../../lib/document/schema/shipments';

import {
  SHIPPING_METHODS,
  SHIPPING_METHODS_PATTERN,
  SHIPPING_METHODS_RECOMMEND_PATTERN
} from '../../../../../../lib/document/schema/shipping_method';

import { SALES_METHOD, SALES_METHOD_PATTERN } from '../../constants/shop';

// ref: lib/document/schema/digital_contents.js
const THIRD_PARTY_ASSET_NAME = {
  NETCH: 'netch_points',
  MANGA_CLUB: 'manga_club_coins',
  SEGACATCHER: 'segacatcher_points'
};

const Shipments = new Schema({
  _id: String,
  // 発送状況
  state: {
    type: String,
    enum: STATE_PATTERN
  },
  // SKU (Shipments.aggregated_items)
  items: [
    {
      // 商品ID
      product: String,
      // レビュー済み
      has_reviewed: {
        type: Boolean,
        default: false
      },
      // 購入SKU
      sku: {
        _id: String,
        no: String,
        titles: {
          en_us: String,
          ja_jp: String
        },
        short_titles: {
          en_us: String,
          ja_jp: String
        },
        image: {
          source: String
        },
        price: Number,
        prices: {
          general: {
            price: Number,
            sale_price: Number
          },
          premium: {
            price: Number,
            sale_price: Number
          }
        },
        purchase_price: Number,
        available_price: Number,
        sales_method: {
          type: String,
          enum: SALES_METHOD_PATTERN
        },
        has_digital_content: Boolean,
        third_party_asset_name: String,
        digital_content: {
          _id: String,
          name: String,
          audio_content: String
        },
        max_purchase_qty: Number,
        expected_ship_at: Date,
        format_expected_ship_at_or_extra_week: String,
        has_premium_price: Boolean,
        region: String,
        serial_codes: {
          type: Array,
          default: []
        },
        has_serial_codes: Boolean,

        // 配送不可か
        undeliverable: {
          type: Boolean,
          default: false
        },

        flags: {
          type: Object,
          default: {}
        }
      },
      // 個数
      qty: Number,

      // ユーザによる配送分割フラグ
      is_divided: Boolean,

      // デジコンギフト（Premium gift）の情報
      gift: {
        _id: String,
        code: String,
        receiver: {
          email: String,
          name: String
        },
        sent_at: Date,
        will_send_at: Date,
        activated_at: Date
      },
      labels: {
        type: Array,
        default: []
      },
      suppliers: {
        type: Array,
        default: []
      },
      children: {
        type: Array,
        default: []
      },

      // 親子SKUの子を分割したitemか
      is_split_from_children: Boolean,

      // 親子SKUの子を分割した新規itemか
      is_split_from_children_and_new: Boolean,

      variation: {
        _id: String,
        title: String,
        imageUrl: String,
        detailUrl: String
      }
    }
  ],

  // ShipmentGroups
  shipmentgroup: {
    type: String,
    ref: 'ShipmentGroups'
  },

  // SHIPPING_METHODS から取得できそう
  average_delivery_time: String,

  // shipment の商品合計金額
  product_total: Number,

  // 商品の合計金額と送料とGiftWrapping料金とImport Feesの合計
  // Shipments.total_with_shipping_charge_and_gift_wrapping_charge_and_import_fees
  shipment_total: Number,

  // 送料
  shipping_charge: Number,

  // 配送の種別
  shipment_type: String,

  // 決済時に徴収されたImport Fees
  import_fees: Number,

  gift_wrapping: {
    type: Boolean,
    default: false
  },
  gift_message: {
    type: String,
    default: ''
  },
  gift_wrapping_total_charge: {
    type: Number,
    default: 0
  },

  // キャンセルリクエスト日またはリファンド日
  canceled_at: Date,

  // キャンセルできるかどうか
  can_cancel: Boolean,

  // 予約商品のskuを含んでいるか
  has_pre_order_sku: Boolean,

  // デジコンが含まれているかどうか
  has_digital_content: Boolean,

  // レビュー済みかどうか
  has_reviewed: {
    type: Boolean,
    default: false
  },

  // 配送不可か
  undeliverable: {
    type: Boolean,
    default: false
  },

  shipping_charges: [
    {
      actual: Number,
      average_delivery_time: String,
      billing: Number,
      discount: Number,
      is_applied_config_for_premium: Boolean,
      point_back: Number,
      recommends: {
        type: String,
        enum: SHIPPING_METHODS_RECOMMEND_PATTERN
      },
      selected: Boolean,
      shipping_method: {
        type: String,
        enum: SHIPPING_METHODS_PATTERN
      },
      import_fees: Number
    }
  ],

  additional_charges: [
    {
      amount: Number,
      created_at: Date
    }
  ],

  // 発送元
  // FIXME: enum
  region: String
});

Shipments.STATE = STATE;
Shipments.THIRD_PARTY_ASSET_NAME = THIRD_PARTY_ASSET_NAME;

// キャンセル済みかどうか
Shipments.isCanceled = function (shipment) {
  return shipment.state === STATE.REFUNDED;
};

// キャンセルリクエスト済みかどうか
Shipments.isCancelRequested = function (shipment) {
  return shipment.state === STATE.CANCEL_REQUESTED;
};

// キャンセル可能なステータスかどうか
Shipments.canCancel = function (shipment) {
  // bto, pre_orderのskuがある場合はキャンセルできない
  if (Shipments.hasPreOrderSku(shipment) || Shipments.hasBtoSku(shipment)) {
    return false;
  }

  const ShipmentGroups = Shipments.dependencyDocuments.ShipmentGroups;
  let shipmentGroup = shipment.shipmentgroup;
  if (typeof shipmentGroup === 'string') {
    shipmentGroup = ShipmentGroups.findById(shipment.shipmentgroup);
  }
  if (
    ShipmentGroups.getShippingStateLabel(shipmentGroup) ===
      ShipmentGroups.DISPLAY_STATE.SHIPPING_SOON ||
    shipmentGroup.state === ShipmentGroups.STATE.STANDBY ||
    shipmentGroup.is_in_process_for_standby
  ) {
    return false;
  }
  return shipment.state === STATE.ORDERED;
};

// Pre-order の商品が含まれているかどうか
Shipments.hasPreOrderSku = function (shipment) {
  return shipment.items.some(function (item) {
    return item.sku.sales_method === SALES_METHOD.PRE_ORDER;
  });
};

// BTO の商品が含まれているかどうか
Shipments.hasBtoSku = function (shipment) {
  return shipment.items.some(function (item) {
    return item.sku.sales_method === SALES_METHOD.BTO;
  });
};

// ShipmentにThird Party Assets のデジタルコンテンツが含まれているかどうか
Shipments.hasThirdPartyAssets = function (shipment) {
  return shipment.items.some((item) => {
    return !!item.sku.third_party_asset_name;
  });
};

// Shipmentにデジタルコンテンツギフトが含まれているかどうか
Shipments.hasDigitalContentGift = function (shipment) {
  return shipment.items.some((item) => {
    return (
      item.gift &&
      item.gift.receiver &&
      item.gift.receiver.name &&
      item.gift.receiver.email
    );
  });
};

// ShipmentsにThird Party Assets のデジタルコンテンツが含まれているかどうか
Shipments.hasThirdPartyAssetsInShipments = function (shipments) {
  return shipments.some((shipment) => {
    return Shipments.hasThirdPartyAssets(shipment);
  });
};

// Shipmentに配送不可アイテムがあるかどうか
Shipments.hasUndeliverableItem = function (shipment) {
  return shipment.items.some(function (item) {
    return item.sku.undeliverable;
  });
};

// Shipmentsに配送不可アイテムあるかどうか
Shipments.hasUndeliverableItemInShipments = function (shipments) {
  return shipments.some((shipment) => {
    return shipment.undeliverable;
  });
};

// 全てのShipmentが配送不可かどうか
Shipments.isAllShipmentsUndeliverable = function (shipments) {
  return shipments.every((shipment) => {
    return shipment.undeliverable;
  });
};

// デジタルコンテンツが含まれているかどうか
Shipments.hasDigitalContent = function (shipment) {
  return !!shipment.has_digital_content;
};

// InStock の商品が含まれているかどうか
Shipments.hasInStockSku = function (shipments) {
  return shipments.some(function (shipment) {
    return shipment.items.some(function (item) {
      return (
        item.sku.sales_method === SALES_METHOD.NORMAL ||
        item.sku.sales_method === SALES_METHOD.CONSIGNMENT
      );
    });
  });
};

// Shipmentsに舞浜発送のshipmentが含まれている
// FIXME: regionをハードコーディングしてしまっているので要修正
Shipments.hasMaihamaShipment = function (shipments) {
  return shipments.some((shipment) => {
    return shipment.region === 'Maihama';
  });
};

// ShipmentsにUS発送のshipmentが含まれている
// FIXME: regionをハードコーディングしてしまっているので要修正
Shipments.hasUSShipment = function (shipments) {
  return shipments.some((shipment) => {
    return shipment.region === 'PDX' || shipment.region === 'Commerce';
  });
};

// Shipmentsの選択可能な配送方法にEMSかAirSPRが含まれている
Shipments.hasEMSOrAirSPRInSelectableShippingMethods = function (shipments) {
  return shipments.some((shipment) => {
    return shipment.shipping_charges.some((charge) => {
      return (
        charge.shipping_method === SHIPPING_METHODS.EMS.value ||
        charge.shipping_method === SHIPPING_METHODS.AIR_SPR.value
      );
    });
  });
};

// 親子SKUの子を分割した新規itemが含まれているかどうか
Shipments.hasNewItemSplitFromChildren = function (shipment) {
  return shipment.items.some((item) => item.is_split_from_children_and_new);
};

// Manga Plusのshipmentで有効な配送方法がないかどうか
Shipments.isNoShippingMethodForMangaPlus = function (shipment) {
  const hasVariation = shipment.items.some(
    (item) => item.variation && item.variation._id
  );
  return (
    hasVariation &&
    shipment.shipping_charges.length === 1 &&
    shipment.shipping_charges[0].shipping_method === 'Air Parcel'
  );
};

// Manga Plusのshipmentで有効な配送方法がないものがあるかどうか
Shipments.hasNoShippingMethodForMangaPlus = function (shipments) {
  return shipments.some((shipment) =>
    Shipments.isNoShippingMethodForMangaPlus(shipment)
  );
};

export default Shipments;
